import { DEFAULT_CHIP_UNIT, type SelectableCurrency } from "@/lib/constants";

const REGION_TO_CURRENCY: Record<string, string> = {
  AE: "AED", AU: "AUD", BR: "BRL", CA: "CAD", CH: "CHF", CN: "CNY",
  CZ: "CZK", DK: "DKK", GB: "GBP", HK: "HKD", HU: "HUF", ID: "IDR",
  IL: "ILS", IN: "INR", JP: "JPY", KR: "KRW", MX: "MXN", MY: "MYR",
  NO: "NOK", NZ: "NZD", PH: "PHP", PL: "PLN", RO: "RON", SA: "SAR",
  SE: "SEK", SG: "SGD", TH: "THB", TR: "TRY", TW: "TWD", US: "USD",
  ZA: "ZAR",
};

const EURO_REGIONS = new Set([
  "AT", "BE", "CY", "DE", "EE", "ES", "FI", "FR", "GR", "HR",
  "IE", "IT", "LT", "LU", "LV", "MT", "NL", "PT", "SI", "SK",
]);

function detectLocale(): string {
  if (typeof navigator !== "undefined") {
    return navigator.languages?.[0] ?? navigator.language ?? "en-US";
  }
  return "en-US";
}

function detectCurrency(locale: string): string {
  try {
    const region = new Intl.Locale(locale).maximize().region;
    if (region) {
      if (EURO_REGIONS.has(region)) return "EUR";
      return REGION_TO_CURRENCY[region] ?? "USD";
    }
  } catch { /* fall through */ }
  return "USD";
}

export function parseAmount(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return parsed;
}

export const appLocale = detectLocale();
export const detectedCurrency = detectCurrency(appLocale);

const numberParts = new Intl.NumberFormat(appLocale).formatToParts(12345.6);
export const decimalSeparator = numberParts.find((part) => part.type === "decimal")?.value ?? ".";
export const groupSeparator = numberParts.find((part) => part.type === "group")?.value ?? ",";

const cashFormatter = new Intl.NumberFormat(appLocale, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const chipFormatter = new Intl.NumberFormat(appLocale, {
  maximumFractionDigits: 0,
});

export function formatCashAmount(value: number): string {
  return cashFormatter.format(value);
}

export function formatChipAmount(value: number): string {
  return chipFormatter.format(Math.round(value));
}

export function formatChipValue(rawValue: string): string {
  if (!rawValue) {
    return "";
  }
  const value = Number(rawValue);
  if (!Number.isFinite(value)) {
    return "";
  }
  return chipFormatter.format(value);
}

export function formatCashValue(rawValue: string): string {
  if (!rawValue) {
    return "";
  }
  const parsed = parseAmount(rawValue);
  if (parsed === null) {
    return "";
  }
  return formatCashAmount(parsed);
}

export function formatCurrency(amount: number, currency: SelectableCurrency): string {
  return new Intl.NumberFormat(appLocale, {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatChipValueCurrency(amount: number, currency: SelectableCurrency): string {
  const maxFractionDigits = Math.max(2, Math.min(6, Math.ceil(-Math.log10(Math.max(amount, 0.000001))) + 1));

  return new Intl.NumberFormat(appLocale, {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 2,
    maximumFractionDigits: maxFractionDigits,
  }).format(amount);
}

export function getCurrencySymbol(currency: SelectableCurrency): string {
  const parts = new Intl.NumberFormat(appLocale, {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
  }).formatToParts(1);
  return parts.find((part) => part.type === "currency")?.value ?? currency;
}

function gcd(a: number, b: number): number {
  let x = Math.abs(Math.trunc(a));
  let y = Math.abs(Math.trunc(b));
  while (y !== 0) {
    const remainder = x % y;
    x = y;
    y = remainder;
  }
  return x;
}

export function detectExactChipUnit(values: number[]): number {
  const positiveIntegers = values
    .map((value) => Math.trunc(value))
    .filter((value) => Number.isFinite(value) && value > 0);

  if (positiveIntegers.length === 0) {
    return 1;
  }

  return positiveIntegers.reduce((acc, value) => gcd(acc, value));
}

export function detectChipUnit(values: number[]): number {
  const positiveIntegers = values
    .map((value) => Math.trunc(value))
    .filter((value) => Number.isFinite(value) && value > 0);

  if (positiveIntegers.length === 0) {
    return 1;
  }

  const detectedUnit = detectExactChipUnit(positiveIntegers);

  // Keep value display anchored to the default chip denomination for
  // typical high-stack games even when one odd stack would force GCD to 1.
  if (detectedUnit < DEFAULT_CHIP_UNIT && Math.max(...positiveIntegers) >= DEFAULT_CHIP_UNIT) {
    return DEFAULT_CHIP_UNIT;
  }

  return detectedUnit;
}

export function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : String(value);
}
